import React, { useState, useEffect } from 'react';
import CardMCFilter from './CardMCFilter';
import { Button, Icon } from 'semantic-ui-react';
import * as XLSX from 'xlsx';
import CardMCVegaChart from './components/CardMCVegaChart';
import { VisualizationSpec } from 'vega-embed';

type CardMCNewUserAgeGenderProps = {
  // Add props if needed
  controller: any;
  runSequence: any;
  MeChartSequence: any;
}

// Component: CardMCNewUserAgeGender
// Purpose: Displays new user demographic charts filtered by Gender, Age, or both Combined.
const CardMCNewUserAgeGender = (props: CardMCNewUserAgeGenderProps) => {
  // State for filtering data
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(12);

  // State for controlling the data view (Gender, Age, or Both)
  const [dataView, setDataView] = useState<'gender' | 'age' | 'combined'>('gender');
  const [isTransposed, setIsTransposed] = useState(false);

  // State for chart data
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Actions
  const onSearch = () => { fetchData(); };
  const onPrint = () => { window.print(); };

  const onDownload = () => {
    if (!chartData || chartData.length === 0) {
      alert('No data to download');
      return;
    }

    let dataToExport = chartData.map(item => ({
      "Age Range": item.age_range,
      "Male": item.male_count,
      "Female": item.female_count,
      "Unknown": item.unknown_count
    }));

    if (isTransposed) {
      const headers = Object.keys(dataToExport[0]);
      const transposedData = [
        headers,
        ...dataToExport.map(row => headers.map((header: string) => (row as any)[header]))
      ];
      const pivotedData = transposedData[0].map((_, colIndex) => transposedData.map(row => row[colIndex]));
      const ws = XLSX.utils.aoa_to_sheet(pivotedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NewUserGenderAgeDistribution");
      XLSX.writeFile(wb, `NewUser_Gender_Age_Distribution_Transposed_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } else {
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "NewUserGenderAgeDistribution");
      XLSX.writeFile(wb, `NewUser_Gender_Age_Distribution_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  // Function to fetch data from API
  const fetchData = async () => {
    setLoading(true);
    try {
      const startDateStr = `${year}-${String(month).padStart(2, '0')}-01 00:00:00`;
      const lastDay = new Date(endYear, endMonth, 0).getDate();
      const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${lastDay} 23:59:59`;

      let res = null
      let data = null
      if (props.MeChartSequence?.sequenceIndex === "Action") {
        res = await props.runSequence({
          sequence: "MeChart", action: "QuerySQL",
          chart: "new-user-age-gender",
          startDate: startDateStr,
          endDate: endDateStr
        })

        console.log('res: ', res);
        if (res?.length > 0 && res?.[0]?.items && Array.isArray(res?.[0]?.items)) {
          data = res?.[0]?.items;
        }
        if (Array.isArray(data)) {
          setChartData(data);
        } else {
          setChartData([]);
        }
        setLoading(false);
      }

      const response = await fetch(`http://localhost:3002/api/new-user-gender-age-distribution?startDate=${startDateStr}&endDate=${endDateStr}`);
      const sqlData = await response.json();
      console.log('sqlData: ', sqlData);

    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);


  // --- Vega Data Transformation ---

  const getVegaGenderData = () => {
    let male = 0;
    let female = 0;
    let unknown = 0;
    chartData.forEach(item => {
      male += parseInt(item.male_count || 0);
      female += parseInt(item.female_count || 0);
      unknown += parseInt(item.unknown_count || 0);
    });
    return [
      { category: 'ชาย (Male)', value: male, color: '#FCE4EC' },
      { category: 'หญิง (Female)', value: female, color: '#F48FB1' },
      { category: 'ไม่ระบุ (Unknown)', value: unknown, color: '#E0E0E0' }
    ];
  };

  const getVegaAgeData = () => {
    const order = ['0-18', '19-24', '25-34', '35-44', '45+'];
    const colors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58', '#FF6D00'];
    const dataMap = new Map();
    chartData.forEach(item => {
      const total = parseInt(item.male_count || 0) + parseInt(item.female_count || 0) + parseInt(item.unknown_count || 0);
      const current = dataMap.get(item.age_range) || 0;
      dataMap.set(item.age_range, current + total);
    });

    return order.map((range, index) => ({
      category: range,
      value: dataMap.get(range) || 0,
      color: colors[index]
    }));
  };

  const getVegaCombinedData = () => {
    const order = ['0-18', '19-24', '25-34', '35-44', '45+'];
    const data: any[] = [];

    order.forEach(range => {
      const item = chartData.find(d => d.age_range === range);
      if (item) {
        data.push({ age: range, gender: 'ชาย (Male)', value: parseInt(item.male_count || 0), color: '#1976D2' });
        data.push({ age: range, gender: 'หญิง (Female)', value: parseInt(item.female_count || 0), color: '#D32F2F' });
        data.push({ age: range, gender: 'ไม่ระบุ (Unknown)', value: parseInt(item.unknown_count || 0), color: '#9E9E9E' });
      } else {
        data.push({ age: range, gender: 'ชาย (Male)', value: 0, color: '#1976D2' });
        data.push({ age: range, gender: 'หญิง (Female)', value: 0, color: '#D32F2F' });
        data.push({ age: range, gender: 'ไม่ระบุ (Unknown)', value: 0, color: '#9E9E9E' });
      }
    });
    return data;
  };

  // --- Vega Specs ---

  const getSpec = (): VisualizationSpec => {
    if (dataView === 'combined') {
      // Combined: Bar Chart
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: getVegaCombinedData() },
        mark: { type: "bar", tooltip: true },
        encoding: {
          [isTransposed ? 'y' : 'x']: {
            field: "age",
            type: "ordinal",
            axis: { title: "Age Range", labelAngle: 0 }
          },
          [isTransposed ? 'x' : 'y']: {
            field: "value",
            type: "quantitative",
            axis: { title: "Count" }
          },
          xOffset: isTransposed ? undefined : { field: "gender" },
          yOffset: isTransposed ? { field: "gender" } : undefined,
          color: {
            field: "gender",
            type: "nominal",
            scale: {
              domain: ['ชาย (Male)', 'หญิง (Female)', 'ไม่ระบุ (Unknown)'],
              range: ['#1976D2', '#D32F2F', '#9E9E9E']
            },
            legend: { title: "Gender" }
          },
          tooltip: [
            { field: "age", title: "Age" },
            { field: "gender", title: "Gender" },
            { field: "value", title: "Count", format: "," }
          ]
        }
      };
    } else {
      // Gender or Age: Pie Chart
      const data = dataView === 'gender' ? getVegaGenderData() : getVegaAgeData();
      return {
        width: "container",
        height: "container",
        autosize: { type: "fit", contains: "padding" },
        data: { values: data },
        layer: [
          {
            mark: { type: "arc", outerRadius: 120, tooltip: true }
          },
          {
            mark: { type: "text", radius: 140 },
            encoding: {
              text: { field: "value", type: "quantitative", format: "," },
              color: { value: "black" }
            }
          }
        ],
        encoding: {
          theta: { field: "value", type: "quantitative", stack: true },
          color: {
            field: "category",
            type: "nominal",
            scale: {
              domain: data.map(d => d.category),
              range: data.map(d => d.color)
            },
            legend: { title: dataView === 'gender' ? "Gender" : "Age Range" }
          },
          order: { field: "value", sort: "descending" },
          tooltip: [
            { field: "category", type: "nominal", title: "Category" },
            { field: "value", type: "quantitative", format: "," }
          ]
        },
        view: { stroke: null }
      };
    }
  };

  return (
    <div id="card-mc-new-user-age-gender">
      {/* Row 1: Toggles (Positions match CardMCOldVsNew) */}
      <div className="controls-container">
        <Button.Group>
          <Button
            active={dataView === 'gender'}
            style={dataView === 'gender' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setDataView('gender')}
          >
            จำนวนสมาชิกแยกตามเพศ รายปี
          </Button>
          <Button
            active={dataView === 'age'}
            style={dataView === 'age' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setDataView('age')}
          >
            จำนวนสมาชิกแยกตามช่วงอายุ รายปี
          </Button>
          <Button
            active={dataView === 'combined'}
            style={dataView === 'combined' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            onClick={() => setDataView('combined')}
          >
            จำนวนสมาชิกแยกตามช่วงอายุและเพศ
          </Button>
        </Button.Group>

        {/* Chart Type Toggles */}
        <Button.Group>
          <Button
            active={dataView === 'gender' || dataView === 'age'}
            disabled={dataView === 'combined'}
            style={dataView !== 'combined' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            icon
          >
            <Icon name='pie graph' /> Pie
          </Button>
          <Button
            active={dataView === 'combined'}
            disabled={dataView !== 'combined'}
            style={dataView === 'combined' ? { backgroundColor: 'var(--primary-theme-color)', color: 'white' } : {}}
            icon
          >
            <Icon name='chart bar' /> Bar
          </Button>
        </Button.Group>
      </div>

      {/* Row 2: Filter and Download */}
      <div className="filter-container">
        <CardMCFilter
          year={year}
          month={month}
          endYear={endYear}
          endMonth={endMonth}
          onYearChange={setYear}
          onMonthChange={setMonth}
          onEndYearChange={setEndYear}
          onEndMonthChange={setEndMonth}
          onSearch={onSearch}
          onPrint={onPrint}
        >
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={onDownload}>
            <Icon name='download' />
            Download .xlsx
          </Button>
          <Button style={{ backgroundColor: 'var(--primary-theme-color)', color: 'white' }} size='small' icon labelPosition='left' onClick={() => setIsTransposed(!isTransposed)} title="Transpose">
            <Icon name='exchange' />
            Transpose
          </Button>
        </CardMCFilter>
      </div>

      {/* Row 4: Chart */}
      <div className="chart-wrapper">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <CardMCVegaChart spec={getSpec()} actions={false} />
        )}
      </div>
    </div>
  );
};

export default CardMCNewUserAgeGender;
